import { useCallback, useEffect, useMemo, type ReactNode } from 'react'
import { Auth0Provider, useAuth0, type AppState } from '@auth0/auth0-react'
import { useNavigate } from 'react-router-dom'
import type { User } from '@/types'
import type { Auth0Config } from '@/config/runtime'
import { AuthContext, type AuthState } from './context'
import { setTokenGetter } from './token'

/** The Auth0 connection name for a Google social connection. Fixed by Auth0. */
const GOOGLE_CONNECTION = 'google-oauth2'

/**
 * Authorization Code + PKCE against the environment's Auth0 tenant (INF-15,
 * INF-16). The SPA is a public client: there is no client secret anywhere in
 * this bundle, and there must not be.
 *
 * TOKEN STORAGE IS IN MEMORY, which is the design on record (docs/00-overview.md
 * §5.1) and has one visible cost worth stating plainly. On a full page reload the
 * SDK has no cached token and falls back to a silent `checkSession` in a hidden
 * iframe; browsers that block third-party cookies block that iframe, so the user
 * lands logged out. Clicking sign in then redirects to Auth0 first-party, finds
 * the live session cookie and returns without a prompt — a flicker, not a
 * re-authentication. Removing the flicker means an Auth0 custom domain on the
 * tenant (so the iframe is first-party), not localStorage: a token in
 * localStorage is readable by any script that gets injected into the page.
 */
export function Auth0AuthProvider({
  config,
  children,
}: {
  config: Auth0Config
  children: ReactNode
}) {
  const navigate = useNavigate()

  // Restores the path the user was heading for before the redirect. `returnTo`
  // was validated by `safeNext` at the call site; it is never taken from the
  // query string here.
  const onRedirectCallback = useCallback(
    (appState?: AppState) => {
      navigate(appState?.returnTo ?? '/', { replace: true })
    },
    [navigate],
  )

  return (
    <Auth0Provider
      domain={config.domain}
      clientId={config.clientId}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        audience: config.audience,
      }}
      onRedirectCallback={onRedirectCallback}
      // Default is 'memory'; stated rather than inherited because the choice is
      // load-bearing and a future SDK default change should not silently move it.
      cacheLocation="memory"
    >
      <Auth0StateBridge>{children}</Auth0StateBridge>
    </Auth0Provider>
  )
}

/** Maps the SDK's state onto the `AuthState` the rest of the app already speaks. */
function Auth0StateBridge({ children }: { children: ReactNode }) {
  const {
    user: auth0User,
    isLoading,
    isAuthenticated,
    error: auth0Error,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0()

  useEffect(() => {
    if (!isAuthenticated) return
    return setTokenGetter(async () => {
      try {
        return (await getAccessTokenSilently()) ?? null
      } catch {
        // A failed renewal is not an error the caller can act on. The next
        // guarded request gets a 401 and the app bounces to /login.
        return null
      }
    })
  }, [isAuthenticated, getAccessTokenSilently])

  const user = useMemo<User | null>(() => {
    if (!isAuthenticated || !auth0User?.email) return null
    // `name` falls back to the email: Auth0 returns the email in `name` for some
    // connections and nothing at all for others, and the app shell renders it.
    return { name: auth0User.name ?? auth0User.email, email: auth0User.email }
  }, [isAuthenticated, auth0User])

  const signIn = useCallback(
    async (next?: string) => {
      await loginWithRedirect({
        appState: { returnTo: next ?? '/' },
        authorizationParams: { connection: GOOGLE_CONNECTION },
      })
    },
    [loginWithRedirect],
  )

  const signOut = useCallback(async () => {
    await logout({ logoutParams: { returnTo: `${window.location.origin}/login` } })
  }, [logout])

  // Deliberately generic, and deliberately not the SDK's message. An Auth0
  // Action denying a non-@waybettermarketing.com account returns a description
  // that names the rule; surfacing it tells an attacker which addresses exist
  // (docs/03-page-specs.md §1). The detail goes to the console for support.
  const error = useMemo(() => {
    if (!auth0Error) return null
    console.error('[auth0]', auth0Error)
    return 'That sign-in did not go through. Try again, or contact your administrator.'
  }, [auth0Error])

  const value = useMemo<AuthState>(
    () => ({ user, loading: isLoading, error, signIn, signOut }),
    [user, isLoading, error, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
